import { useLayoutEffect, useState, type ReactNode } from 'react'
import { createPortal } from 'react-dom'
import { PageBack } from './PageBack'

/**
 * Tiêu đề trang: nút quay lại, hình, tên màn hình.
 *
 * Ở bộ Modern nó <b>không vẽ trong trang</b> mà nhảy lên thanh trên, vào đúng chỗ cái
 * logo vẫn ngồi. Lý do là chỗ trống: thanh trên vốn chỉ mang một cái logo và một câu giới
 * thiệu — hai thứ người bán đã đọc xong từ hôm cài app — trong khi mỗi màn hình lại tốn
 * thêm một hàng cao 56px chỉ để nhắc mình đang ở đâu. Gộp hai thứ vào một hàng là trả lại
 * gần 80px cho bàn và món, thứ người ta thật sự nhìn cả ngày.
 *
 * Bộ Classic không có thanh trên nào cả, nên ở đó nó vẽ tại chỗ như một thanh tiêu đề
 * bình thường. Cùng một thành phần, hai chỗ đứng — nơi gọi không cần biết mình đang ở bộ
 * nào.
 */

/** Ô trống trên thanh trên mà tiêu đề trang nhảy vào. */
export const PAGE_HEAD_SLOT = 'page-head-slot'

interface Props {
  title: string
  sub?: ReactNode
  /** Hình của màn hình. Bọc sẵn trong ô vuông bo góc, nơi gọi chỉ truyền cái hình. */
  icon?: ReactNode
  /** Thêm màu cho ô hình: `is-blue`, `is-amber`, `is-violet`, `is-green`. */
  tone?: string
  /** Đường quay về. Không truyền `to` lẫn `onBack` thì màn hình này không có nút quay lại. */
  to?: string
  onBack?: () => void
  /** Nút nhỏ đi liền với tiêu đề. Dùng dè — thanh trên chỉ cao 66px. */
  right?: ReactNode
}

export function PageHead({ title, sub, icon, tone, to, onBack, right }: Props) {
  const [slot, setSlot] = useState<HTMLElement | null>(null)

  // `useLayoutEffect` chứ không phải `useEffect`: chạy xong trước khi trình duyệt vẽ, nên
  // tiêu đề không kịp loé lên trong trang rồi mới nhảy lên thanh.
  useLayoutEffect(() => {
    setSlot(document.getElementById(PAGE_HEAD_SLOT))
  }, [])

  const head = (
    <div className={slot ? 'page-head' : 'appbar page-head'}>
      {(to || onBack) && <PageBack to={to} onBack={onBack} />}

      {icon && <span className={`page-icon${tone ? ` ${tone}` : ''}`} aria-hidden="true">{icon}</span>}

      <div className="page-title">
        <h1>{title}</h1>
        {sub && <p>{sub}</p>}
      </div>

      {right}
    </div>
  )

  return slot ? createPortal(head, slot) : head
}
